// ─────────────────────────────────────────────
//  TimelineRail — column 1 of the Timeline Chart page. A 179px vertical rail.
//
//  Purely presentational: every value arrives as a prop so the whole thing is
//  testable without Firestore. TimelineChartPage owns the data.
//
//  THE ORDER IS THE SAFETY PROPERTY. The trust block sits ABOVE the counts, so a
//  reader cannot reach a "0" without first crossing the line that says whether
//  this screen is live and which campus it is showing. A zero under a dead feed
//  and a zero on a quiet board are the same three pixels; the only thing that
//  tells them apart is what sits above them.
//
//  Row slots NEVER reflow. The four alarm rows are always present, always in the
//  same order, always the same height. A row that appears when something is wrong
//  trains the eye to read layout instead of content, and a row that disappears
//  when data is missing looks identical to "nothing is wrong".
// ─────────────────────────────────────────────
import React from "react";
import RailEngineSwitch from "./RailEngineSwitch";
import tokens from "../../theme/tokens";
import DoctorsNeededBox from "./DoctorsNeededBox";

const { color, font, radius } = tokens;

export default function TimelineRail({
  facilityName,
  // The Google Sheet engine: its state, and whether this viewer may stop it.
  // Absent for a facility that does not run one — see RailEngineSwitch.
  engineState = null,
  canToggleEngine = false,
  engineBusy = false,
  onToggleEngine,
  dateKey,
  clockLabel,
  connection,
  alertsCovered = true,
  alertHomeFacilityName,
  locationLine,
  capacity,
  providers,
  // { now, steps, releasable } from domain/doctorSteps, via useControlPanelData.
  doctorPlan = null,
  uncovered = null,
  uncoveredUnavailable = false,
  planned = false,
  canEditDoctors = false,
  doctorsEditLockReason = "",
  onExtraDoctor = null,
  // Built by the page — releasing a doctor needs the roster, the assignments and
  // the staff pool, none of which belong in a rail. Passed straight through.
  releaseMenu = null,
  releaseOpen = false,
}) {
  // ⚠️ THREE BANDS, NOT TWO. `connectionState` returns a `tone` of ok | warn |
  // bad across five levels (panelConnection.js:76-88). This used to test
  // `level === "live"` and paint everything else in full alarm red — so a
  // sixty-second blip, which that module documents as "brief blip; say so
  // quietly", turned the entire trust block the same colour as a board that has
  // stopped telling the truth. Spending the loudest treatment on the mildest
  // state is how people learn to ignore it.
  const tone = connection?.tone || (connection?.level === "live" ? "ok" : "warn");
  const trustBg = tone === "ok" ? "#0d0f1a" : tone === "warn" ? "#5b3a09" : "#7f1d1d";
  const pillBg = tone === "ok" ? "#062e24" : tone === "warn" ? "#fef3c7" : "#fee2e2";
  const pillInk = tone === "ok" ? "#6ee7b7" : tone === "warn" ? "#7a4f00" : "#7f1d1d";
  return (
    <aside style={s.rail} aria-label="Board status">
      {/* ── TRUST BLOCK ─────────────────────────────────────────────────
          The only inverted surface on the page, and the only block whose
          GROUND COLOUR carries meaning: amber while reconnecting, red once the
          board has stopped being trustworthy. A badge can be missed; a whole
          panel changing colour cannot. */}
      <div style={{ ...s.trust, background: trustBg }}>
        <div style={s.trustDate}>{dateKey}</div>
        <div style={s.trustClock}>{clockLabel}</div>
        <div
          style={{ ...s.trustPill, background: pillBg, color: pillInk }}
          data-testid="rail-connection"
          data-level={connection?.level}
        >
          <span aria-hidden="true">{connection?.glyph || "○"}</span>{" "}
          {connection?.rail || "CONNECTING…"}
        </div>
        <div style={s.trustFacility}>{facilityName}</div>
        {locationLine && <div style={s.trustLocation}>{locationLine}</div>}

        {/* ⚠️ THE ENGINE IS A SEPARATE FACT FROM THE CONNECTION ABOVE IT, and
            it sits here because this is the block people look at to decide
            whether to trust the board. The connection says whether THIS BROWSER
            is current; the engine says whether the SHEET is still being applied
            to the board at all. Either can be healthy while the other is not. */}
        <RailEngineSwitch
          state={engineState}
          canToggle={canToggleEngine}
          busy={engineBusy}
          onToggle={onToggleEngine}
        />
        {/* Naming the campus the alerts DO cover is the difference between
            "something is broken" and "you are looking somewhere else". */}
        {!alertsCovered && (
          <div style={s.trustWarn}>
            VIEWING ANOTHER CAMPUS
            {alertHomeFacilityName ? ` · ALERTS FOLLOW ${alertHomeFacilityName.toUpperCase()}` : ""}
          </div>
        )}
      </div>

      {/* ── DOCTORS NEEDED ─────────────────────────────────────────────
          The rail's only status box, and the only place on it that spends ink.
          It replaced four fixed chips: EMERGENCIES and BREAKS WAITING moved to
          TimelineStatusBand (which never collapses away), UNCOVERED lives on as
          a line INSIDE the box because it had nowhere else to go, and the old
          SHORT n AT 3 PM chip is the same maths this box now does at the day's
          real crossings.

          It is not alert-derived, so it keeps reporting on any campus. Only its
          uncovered line refuses. */}
      <DoctorsNeededBox
        now={doctorPlan?.now ?? null}
        steps={doctorPlan?.steps}
        releasable={doctorPlan?.releasable ?? null}
        uncovered={uncovered}
        uncoveredUnavailable={uncoveredUnavailable}
        uncoveredHomeFacilityName={alertHomeFacilityName}
        planned={planned}
        canEdit={canEditDoctors}
        editLockReason={doctorsEditLockReason}
        onExtraDoctor={onExtraDoctor}
        releaseMenu={releaseMenu}
        releaseOpen={releaseOpen}
      />

      {!alertsCovered && (
        <p style={s.refusal}>
          Alerts are not available for this facility. They follow your signed-in
          campus, so emergencies and break requests are not shown here.
        </p>
      )}

      {/* ── CAPACITY ───────────────────────────────────────────────────
          Rooms, then people. No colour anywhere in this block: it is reference,
          not alarm. */}
      <div style={s.card}>
        <div style={s.cardLabel}>ROOMS RUNNING</div>
        <div style={s.bigNumber}>
          {capacity?.roomsRunning ?? "—"}
          <span style={s.bigNumberUnit}> of {capacity?.roomsBuilt ?? "—"} built</span>
        </div>

        <div style={s.divider} />
        <div style={s.cardLabel}>PROVIDERS</div>
        <div style={s.midNumber}>
          {providers?.onShift ?? "—"}
          <span style={s.bigNumberUnit}> on shift</span>
        </div>
        <div style={s.subLine}>
          {providers?.bench ?? 0} on the bench
        </div>
      </div>
    </aside>
  );
}

const s = {
  // ⚠️ NOT STICKY. STICKINESS IS THE COLUMN'S JOB, NOT THIS CARD'S.
  // This used to carry `position: sticky; top: 12` itself. On desktop that was
  // harmless — TimelineChartPage wraps the whole column in its own sticky
  // wrapper, which pins first — but the page only applies that wrapper when the
  // layout is SIDE BY SIDE. Below 1280px it stacks, the wrapper is dropped, and
  // this rule became live: the rail was displaced 12px down its own flow
  // position at every scroll offset, which swallowed the 8px gap under it and
  // painted the last 4px of the rail over the MD CALL card below (measured
  // 2026-08-15 at 360px: rail bottom 500, MD CALL top 496). A sticky element
  // also creates a stacking context, so the card underneath lost, silently.
  // `alignSelf: "start"` stays: in the desktop grid cell it stops the rail
  // stretching to the chart's full height.
  rail: { display: "flex", flexDirection: "column", gap: 8, alignSelf: "start" },

  trust: { borderRadius: radius.md, padding: "10px 10px 12px", color: "#fff" },
  trustDate: { fontSize: 10, letterSpacing: ".8px", opacity: 0.75, fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" },
  // Big enough to read from across a room, which is the actual use.
  trustClock: { fontSize: 26, fontWeight: font.weight.bold, lineHeight: 1.05, margin: "2px 0 6px" },
  trustPill: { display: "inline-block", fontSize: 10, fontWeight: font.weight.bold, letterSpacing: ".5px", padding: "3px 7px", borderRadius: radius.full },
  trustFacility: { fontSize: 12, fontWeight: font.weight.bold, marginTop: 8, lineHeight: 1.25 },
  trustLocation: { fontSize: 9.5, letterSpacing: ".4px", opacity: 0.7, marginTop: 3 },
  trustWarn: { fontSize: 9.5, fontWeight: font.weight.bold, letterSpacing: ".4px", color: "#fde68a", marginTop: 6, lineHeight: 1.35 },

  refusal: { fontSize: 10, lineHeight: 1.4, color: "#7a4f00", background: "#fef8ec", border: `1px solid ${color.accent.closing}`, borderRadius: radius.sm, padding: "7px 8px", margin: 0 },

  card: { background: color.surface.card, border: `1px solid ${color.border.default}`, borderRadius: radius.md, padding: "9px 10px 11px" },
  cardLabel: { fontSize: 9, fontWeight: font.weight.bold, letterSpacing: ".7px", color: color.text.muted },
  bigNumber: { fontSize: 21, fontWeight: font.weight.bold, color: color.text.primary, lineHeight: 1.1, marginTop: 2 },
  midNumber: { fontSize: 17, fontWeight: font.weight.bold, color: color.text.primary, lineHeight: 1.15, marginTop: 2 },
  bigNumberUnit: { fontSize: 10.5, fontWeight: font.weight.regular, color: color.text.secondary, letterSpacing: 0 },
  subLine: { fontSize: 10.5, color: color.text.secondary, marginTop: 2 },
  divider: { height: 1, background: color.border.default, margin: "9px 0" },      
};
